import React, { useEffect, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import type { Bed, MapMode, Plant } from '../../types';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { FloretLookup } from '../FloretLookup';
import { todayISO } from '../../lib/utils';

const TYPES = ['annual','perennial','shrub','tree','herb','vegetable','grass','bulb','groundcover','other'] as const;
const STATUSES = ['planned','planted','thriving','struggling','removed','dead'] as const;
const SUN = ['full_sun','part_shade','full_shade','varies'] as const;

interface PlantFormProps {
  initial?: Partial<Plant>;
  beds: Bed[];
  pendingX?: number | null;
  pendingY?: number | null;
  mapMode: MapMode;
  onSetMapMode: (m: MapMode) => void;
  onSubmit: (data: Partial<Plant>) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function PlantForm({
  initial,
  beds,
  pendingX,
  pendingY,
  mapMode,
  onSetMapMode,
  onSubmit,
  onCancel,
  isEdit,
}: PlantFormProps) {
  const [form, setForm] = useState<Partial<Plant>>({
    common_name: '',
    botanical_name: '',
    cultivar: '',
    plant_type: 'other',
    status: 'planned',
    bed_id: '',
    planted_date: todayISO(),
    sun_needs: '',
    water_needs: '',
    mature_height: undefined,
    mature_spread: undefined,
    notes: '',
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Sync location when user taps the map in add-plant mode
  useEffect(() => {
    if (pendingX != null && pendingY != null) {
      setForm((f) => ({ ...f, x: pendingX, y: pendingY }));
    }
  }, [pendingX, pendingY]);

  const set = (key: keyof Plant, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.common_name?.trim()) { setError('Common name is required'); return; }
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        bed_id: form.bed_id || null,
        botanical_name: form.botanical_name || null,
        cultivar: form.cultivar || null,
        sun_needs: form.sun_needs || null,
        water_needs: form.water_needs || null,
        notes: form.notes || null,
        planted_date: form.planted_date || null,
        removed_date: form.removed_date || null,
        mature_height: form.mature_height || null,
        mature_spread: form.mature_spread || null,
      });
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <div>
        <Input
          label="Common Name *"
          value={form.common_name ?? ''}
          onChange={(e) => set('common_name', e.target.value)}
          placeholder="e.g. Cafe Au Lait Dahlia"
          required
        />
        <FloretLookup
          initialQuery={form.common_name ?? ''}
          onSelect={(data) => {
            setForm((f) => ({
              ...f,
              common_name: data.title,
              plant_type: data.plant_type,
              // Prepend Floret info to any existing notes
              notes: data.notes + (f.notes ? '\n\n' + f.notes : ''),
            }));
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Botanical Name"
          value={form.botanical_name ?? ''}
          onChange={(e) => set('botanical_name', e.target.value)}
          placeholder="e.g. Rudbeckia hirta"
        />
        <Input
          label="Variety / Cultivar"
          value={form.cultivar ?? ''}
          onChange={(e) => set('cultivar', e.target.value)}
          placeholder="e.g. Goldsturm"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Select label="Type" value={form.plant_type ?? 'other'} onChange={(e) => set('plant_type', e.target.value)}>
          {TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </Select>
        <Select label="Status" value={form.status ?? 'planned'} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </Select>
      </div>

      <Select label="Garden Bed" value={form.bed_id ?? ''} onChange={(e) => set('bed_id', e.target.value)}>
        <option value="">No bed assigned</option>
        {beds.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </Select>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Planted Date"
          type="date"
          value={form.planted_date ?? ''}
          onChange={(e) => set('planted_date', e.target.value)}
        />
        <Input
          label="Removed Date"
          type="date"
          value={form.removed_date ?? ''}
          onChange={(e) => set('removed_date', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Sun Needs"
          value={form.sun_needs ?? ''}
          onChange={(e) => set('sun_needs', e.target.value)}
          placeholder="e.g. Full sun"
        />
        <Input
          label="Water Needs"
          value={form.water_needs ?? ''}
          onChange={(e) => set('water_needs', e.target.value)}
          placeholder="e.g. Low"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Mature Height (m)"
          type="number"
          step="0.1"
          min="0"
          value={form.mature_height ?? ''}
          onChange={(e) => set('mature_height', e.target.value ? parseFloat(e.target.value) : undefined)}
          placeholder="0.6"
        />
        <Input
          label="Mature Spread (m)"
          type="number"
          step="0.1"
          min="0"
          value={form.mature_spread ?? ''}
          onChange={(e) => set('mature_spread', e.target.value ? parseFloat(e.target.value) : undefined)}
          placeholder="0.6"
        />
      </div>

      {/* Map location picker */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Map Location</p>
        {form.x != null && form.y != null ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-garden-600 shrink-0" />
              <span className="text-sm text-gray-600 dark:text-gray-300 flex-1">Location set</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => onSetMapMode('add-plant')}>
                Move on map
              </Button>
              <button
                type="button"
                onClick={() => { set('x', undefined); set('y', undefined); }}
                aria-label="Clear location"
                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label htmlFor="plant-x" className="text-gray-500 dark:text-gray-400 w-4">X</label>
              <input
                id="plant-x"
                type="number"
                value={Math.round(form.x)}
                onChange={(e) => set('x', Number(e.target.value))}
                className="w-24 px-2 py-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:border-garden-400"
              />
              <label htmlFor="plant-y" className="text-gray-500 dark:text-gray-400 w-4">Y</label>
              <input
                id="plant-y"
                type="number"
                value={Math.round(form.y!)}
                onChange={(e) => set('y', Number(e.target.value))}
                className="w-24 px-2 py-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:border-garden-400"
              />
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant={mapMode === 'add-plant' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onSetMapMode(mapMode === 'add-plant' ? 'view' : 'add-plant')}
          >
            <MapPin size={14} />
            {mapMode === 'add-plant' ? 'Click the map to place…' : 'Pick location on map'}
          </Button>
        )}
      </div>

      <Textarea
        label="Notes"
        value={form.notes ?? ''}
        onChange={(e) => set('notes', e.target.value)}
        placeholder="Growth notes, observations…"
        rows={2}
      />

      <div className="flex gap-2 pt-1">
        <Button type="submit" loading={saving} className="flex-1">
          {isEdit ? 'Save Changes' : 'Add Plant'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
